import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import apiClient from "@/lib/api-client";
import { useZoteroWatchStore } from "@/stores/zotero-watch-store";

/** How long to keep checking after a library click, and how often. The
 * server rate-limits poll-now to 1 per 30s per user, so checks never go
 * out closer together than that. */
export const WATCH_WINDOW_MS = 5 * 60_000;
const CHECK_EVERY_MS = 30_000;

export type ZoteroWatch = "off" | "waiting" | "timedOut";

/** Feedback for a "Check now" click: queued or in flight, then the time the
 * check went through. */
export type ManualCheck = { status: "idle" } | { status: "checking" } | { status: "checked"; at: number };

const isRateLimited = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 429;

/** After "Get it through your library" with Zotero connected, the PDF
 * arrives only once the user saves it with the Zotero Connector — usually
 * in another tab, a minute later. The scheduled poll runs every 5 min, so
 * while a watch is fresh this checks Zotero when the user comes back to
 * the tab and every 30s, and refreshes the matched-events list so a new
 * match invalidates the paper queries (useZoteroSyncEvents). */
export function useZoteroWatch(paperId: string, hasPdf: boolean) {
  const qc = useQueryClient();
  const startedAt = useZoteroWatchStore((s) => s.startedAt[paperId]);
  const start = useZoteroWatchStore((s) => s.start);
  const stop = useZoteroWatchStore((s) => s.stop);
  const markChecked = useZoteroWatchStore((s) => s.markChecked);
  const [now, setNow] = useState(() => Date.now());
  const [manual, setManualState] = useState<ManualCheck>({ status: "idle" });
  // Read from timer callbacks, which would otherwise see a stale `manual`.
  const manualRef = useRef<ManualCheck>(manual);
  const setManual = useCallback((m: ManualCheck) => {
    manualRef.current = m;
    setManualState(m);
  }, []);
  const queued = useRef<ReturnType<typeof setTimeout> | null>(null);

  const active = startedAt != null && !hasPdf && now - startedAt < WATCH_WINDOW_MS;

  const clearQueued = () => {
    if (queued.current) clearTimeout(queued.current);
    queued.current = null;
  };

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["zotero-sync-poll-status"] });
    qc.invalidateQueries({ queryKey: ["zotero-sync-events", "matched"] });
  }, [qc]);

  /** Asks the server to check Zotero now; resolves false if rate-limited. */
  const send = useCallback(async () => {
    markChecked();
    try {
      await apiClient.post("api/zotero-sync/poll-now");
    } catch (e) {
      if (isRateLimited(e)) return false;
      // 409 (no source) and the like: nothing more a retry would do.
    }
    refresh();
    return true;
  }, [markChecked, refresh]);

  /** Tick / tab-focus check: skipped when any check went out in the last
   * 30s. A check that goes through also answers a queued "Check now". */
  const autoCheck = useCallback(() => {
    if (Date.now() - useZoteroWatchStore.getState().lastCheckAt < CHECK_EVERY_MS) return;
    void send().then((ok) => {
      if (ok && manualRef.current.status === "checking") {
        clearQueued();
        setManual({ status: "checked", at: Date.now() });
      }
    });
  }, [send, setManual]);

  /** "Check now": waits out the rest of the 30s window, then checks; a 429
   * (another tab got there first) waits one more window. */
  const runManual = useCallback(() => {
    const run = () => {
      queued.current = null;
      const wait = useZoteroWatchStore.getState().lastCheckAt + CHECK_EVERY_MS - Date.now();
      if (wait > 0) {
        queued.current = setTimeout(run, wait);
        return;
      }
      void send().then((ok) => {
        if (manualRef.current.status !== "checking") return; // stopped meanwhile
        if (ok) setManual({ status: "checked", at: Date.now() });
        else queued.current = setTimeout(run, CHECK_EVERY_MS);
      });
    };
    run();
  }, [send, setManual]);

  useEffect(() => clearQueued, []);

  useEffect(() => {
    if (hasPdf && startedAt != null) stop(paperId);
  }, [hasPdf, startedAt, paperId, stop]);

  useEffect(() => {
    if (!active) return;
    const tick = setInterval(() => {
      setNow(Date.now());
      autoCheck();
    }, CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") autoCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [active, autoCheck]);

  // Re-render once when the window closes, so "timed out" shows.
  useEffect(() => {
    if (startedAt == null || hasPdf) return;
    const left = startedAt + WATCH_WINDOW_MS - Date.now();
    if (left <= 0) return;
    const t = setTimeout(() => setNow(Date.now()), left);
    return () => clearTimeout(t);
  }, [startedAt, hasPdf]);

  const state: ZoteroWatch =
    startedAt == null || hasPdf ? "off" : active ? "waiting" : "timedOut";
  return {
    state,
    manual,
    /** Called on the library click, which has just asked the server to
     * check (useLibraryLink.notifyZotero), so that counts as a check. */
    start: () => {
      markChecked();
      start(paperId);
    },
    checkNow: () => {
      start(paperId); // a manual check restarts the window
      setNow(Date.now());
      if (queued.current) return; // already queued
      setManual({ status: "checking" });
      runManual();
    },
    stop: () => {
      clearQueued();
      setManual({ status: "idle" });
      stop(paperId);
    },
  };
}
